import { serializeTradeLogicSeedCondition, TRADE_LOGIC_SEED_CONDITION_PARAM_KEY, type TradeLogicSeedVetoCondition } from './seedCondition';

export type TradeLogicEditorKind = 'entry' | 'exit';

export interface TradeLogicAttachmentReturnState {
    kind: TradeLogicEditorKind;
    definitionId: number;
    version: number;
    versionId: number;
}

export interface TradeLogicEditorLoadSource {
    definitionId: number;
    mode: 'copyFromPublished' | 'definition';
    version: number | null;
}

export interface BuildTradeLogicEditorPathOptions {
    copyFromDefinitionId?: number | null;
    copyFromVersion?: number | null;
    version?: number | null;
    /** Veto condition to pre-add to the copied draft (regime-profile "Create gated variant" flow). */
    seedCondition?: TradeLogicSeedVetoCondition | null;
}

const ATTACH_QUERY_PARAM_KEYS = [
    'attachTradeLogicKind',
    'attachTradeLogicDefinitionId',
    'attachTradeLogicVersion',
    'attachTradeLogicVersionId'
] as const;

const STRATEGY_EDITOR_PATH_PATTERN = /^\/user\/tradingStrategy\/([^/?#]+)/;
const TRADE_LOGIC_NAVIGATION_BASE_URL = 'https://project314.local';

const parsePositiveInteger = (raw: string | null | undefined): number | null => {
    if (!raw) return null;

    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : null;
};

export const buildTradeLogicEditorPath = (
    kind: TradeLogicEditorKind,
    id: number | 'new',
    returnTo: string,
    options: BuildTradeLogicEditorPathOptions = {}
) => {
    const cleanedReturnTo = sanitizeTradeLogicReturnTo(returnTo) ?? returnTo;

    const params = new URLSearchParams({
        attachKind: kind,
        returnTo: cleanedReturnTo
    });

    if (options.version != null && options.version > 0) {
        params.set('version', String(options.version));
    }

    if (options.copyFromDefinitionId != null && options.copyFromDefinitionId > 0) {
        params.set('copyFromDefinitionId', String(options.copyFromDefinitionId));
    }

    if (options.copyFromVersion != null && options.copyFromVersion > 0) {
        params.set('copyFromVersion', String(options.copyFromVersion));
    }

    if (options.seedCondition != null) {
        params.set(TRADE_LOGIC_SEED_CONDITION_PARAM_KEY, serializeTradeLogicSeedCondition(options.seedCondition));
    }

    return `/user/tradeLogic/${kind}/${id}?${params.toString()}`;
};

const buildRelativeNavigationTarget = (pathname: string, searchParams: URLSearchParams, hash: string): string => {
    const search = searchParams.toString();
    return `${pathname}${search ? `?${search}` : ''}${hash}`;
};

export const stripTradeLogicAttachmentSearchParams = (searchParams: URLSearchParams): URLSearchParams => {
    const cleanedSearchParams = new URLSearchParams(searchParams);

    ATTACH_QUERY_PARAM_KEYS.forEach((key) => {
        cleanedSearchParams.delete(key);
    });

    return cleanedSearchParams;
};

export const sanitizeTradeLogicReturnTo = (returnTo: string | null): string | null => {
    if (returnTo === null) return null;

    // Direct publish/apply flows navigate back through returnTo. Strip any
    // stale attachment replay params here so an older attachment cannot be
    // re-applied on the way back to the strategy editor.
    const parsedUrl = new URL(returnTo, TRADE_LOGIC_NAVIGATION_BASE_URL);
    const cleanedSearchParams = stripTradeLogicAttachmentSearchParams(parsedUrl.searchParams);

    return buildRelativeNavigationTarget(parsedUrl.pathname, cleanedSearchParams, parsedUrl.hash);
};

export const resolveTradeLogicEditorLoadSource = ({
    routeDefinitionId,
    searchParams
}: {
    routeDefinitionId: number | null;
    searchParams: URLSearchParams;
}): TradeLogicEditorLoadSource | null => {
    if (routeDefinitionId !== null) {
        return {
            definitionId: routeDefinitionId,
            mode: 'definition',
            version: parsePositiveInteger(searchParams.get('version'))
        };
    }

    const copyFromDefinitionId = parsePositiveInteger(searchParams.get('copyFromDefinitionId'));

    if (copyFromDefinitionId === null) {
        return null;
    }

    return {
        definitionId: copyFromDefinitionId,
        mode: 'copyFromPublished',
        version: parsePositiveInteger(searchParams.get('copyFromVersion'))
    };
};

export const buildTradeLogicReturnNavigationTarget = ({
    attachKind,
    kind,
    result,
    returnTo
}: {
    attachKind: string | null;
    kind: TradeLogicEditorKind | null;
    result: { definitionId: number; version: number; versionId: number };
    returnTo: string | null;
}): { pathname: string; search: string } | null => {
    const cleanedReturnTo = sanitizeTradeLogicReturnTo(returnTo);

    if (cleanedReturnTo === null || kind === null || attachKind !== kind) return null;

    const [pathname, rawSearch = ''] = cleanedReturnTo.split('?');
    const nextSearchParams = new URLSearchParams(rawSearch);

    nextSearchParams.set('attachTradeLogicKind', kind);
    nextSearchParams.set('attachTradeLogicDefinitionId', String(result.definitionId));
    nextSearchParams.set('attachTradeLogicVersion', String(result.version));
    nextSearchParams.set('attachTradeLogicVersionId', String(result.versionId));

    return {
        pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : ''
    };
};

export type TradeLogicEditorContext =
    | {
          mode: 'library';
          strategyId: null;
      }
    | {
          mode: 'strategy';
          strategyId: number | null;
      };

export const resolveTradeLogicEditorContext = ({
    attachKind,
    kind,
    returnTo
}: {
    attachKind: string | null;
    kind: TradeLogicEditorKind | null;
    returnTo: string | null;
}): TradeLogicEditorContext => {
    const cleanedReturnTo = sanitizeTradeLogicReturnTo(returnTo);

    if (cleanedReturnTo === null || kind === null || attachKind !== kind) {
        return {
            mode: 'library',
            strategyId: null
        };
    }

    const [pathname] = cleanedReturnTo.split('?');
    const strategyId = resolveStrategyIdFromPathname(pathname);

    if (strategyId === undefined) {
        return {
            mode: 'library',
            strategyId: null
        };
    }

    if (strategyId === null) {
        return {
            mode: 'strategy',
            strategyId: null
        };
    }

    return {
        mode: 'strategy',
        strategyId
    };
};

export const resolveStrategyIdFromPathname = (pathname: string): number | null | undefined => {
    const match = pathname.match(STRATEGY_EDITOR_PATH_PATTERN);

    if (!match) {
        return undefined;
    }

    if (match[1] === 'new') {
        return null;
    }

    return parsePositiveInteger(match[1]);
};

export const consumeTradeLogicAttachmentSearchParams = (
    searchParams: URLSearchParams
): {
    attachment: TradeLogicAttachmentReturnState | null;
    cleanedSearchParams: URLSearchParams;
    hadAttachParams: boolean;
} => {
    const cleanedSearchParams = stripTradeLogicAttachmentSearchParams(searchParams);
    let hadAttachParams = false;

    ATTACH_QUERY_PARAM_KEYS.forEach((key) => {
        if (searchParams.has(key)) {
            hadAttachParams = true;
        }
    });

    if (!hadAttachParams) {
        return {
            attachment: null,
            cleanedSearchParams,
            hadAttachParams: false
        };
    }

    const kind = searchParams.get('attachTradeLogicKind');

    if (kind !== 'entry' && kind !== 'exit') {
        return {
            attachment: null,
            cleanedSearchParams,
            hadAttachParams: true
        };
    }

    const definitionId = parsePositiveInteger(searchParams.get('attachTradeLogicDefinitionId'));
    const version = parsePositiveInteger(searchParams.get('attachTradeLogicVersion'));
    const versionId = parsePositiveInteger(searchParams.get('attachTradeLogicVersionId'));

    if (definitionId === null || version === null || versionId === null) {
        return {
            attachment: null,
            cleanedSearchParams,
            hadAttachParams: true
        };
    }

    return {
        attachment: {
            kind,
            definitionId,
            version,
            versionId
        },
        cleanedSearchParams,
        hadAttachParams: true
    };
};
